import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './MembershipModelDemo.css';

/**
 * MembershipModelDemo — standalone, client-only sandbox for the
 * anchor / extending membership model (docs/anchor-and-extending-memberships.md).
 *
 * Everything on this page is in-memory mock data. No API calls, no real
 * groups/UGP rows are touched. It exists so the model can be evaluated
 * interactively before Phase 1+ is implemented.
 */

const ORGS = {
  schoolA: { id: 'schoolA', name: 'School A' },
  schoolB: { id: 'schoolB', name: 'School B' }
};

// kind: 'anchor' | 'extending'. structural = extending group used purely for
// tree organization (no direct members expected).
const INITIAL_GROUPS = [
  { id: 'g-students', orgId: 'schoolA', parentId: null, name: 'Students', kind: 'anchor', periodLabel: '2026–2027' },
  { id: 'g-exchange', orgId: 'schoolA', parentId: null, name: 'Exchange Students', kind: 'anchor', periodLabel: '2026–2027' },
  { id: 'g-chess', orgId: 'schoolA', parentId: 'g-students', name: 'Chess Club', kind: 'extending', periodLabel: '2026–2027' },
  { id: 'g-sports', orgId: 'schoolA', parentId: null, name: 'Sports', kind: 'extending', structural: true },
  { id: 'g-basketball', orgId: 'schoolA', parentId: 'g-sports', name: 'Basketball', kind: 'extending', structural: true },
  { id: 'g-teama', orgId: 'schoolA', parentId: 'g-basketball', name: 'Team A', kind: 'extending', structural: true },
  { id: 'g-coaches', orgId: 'schoolA', parentId: 'g-teama', name: 'Coaches', kind: 'anchor', periodLabel: 'Permanent' },
  { id: 'g-roster', orgId: 'schoolA', parentId: 'g-teama', name: 'Roster', kind: 'extending', periodLabel: 'Fall 2026' },
  { id: 'g-robotics', orgId: 'schoolA', parentId: null, name: 'Robotics Club', kind: 'extending', periodLabel: '2026–2027' },
  { id: 'g-teachers-b', orgId: 'schoolB', parentId: null, name: 'Teachers', kind: 'anchor', periodLabel: 'Permanent' }
];

// Apps assigned to a group's gp → drives how rich its profile section is.
const GROUP_APPS = {
  'g-roster': ['Teams', 'Stats', 'Schedule']
};

// Page-permission grants attached to a group's gp.
const GROUP_GRANTS = {
  'g-students': ['dashboard', 'profile', 'work-samples'],
  'g-exchange': ['dashboard', 'profile'],
  'g-coaches': ['dashboard', 'groups (team roster)'],
  'g-teachers-b': ['dashboard', 'forms'],
  'g-roster': ['schedules (team page)']
};

const INITIAL_USERS = [
  { id: 'u-jordan', name: 'Jordan Reyes', blurb: 'Student · plays basketball, chess, on Dean\u2019s List' },
  { id: 'u-maya', name: 'Maya Chen', blurb: 'Exchange student · joined Chess Club across anchors' },
  { id: 'u-dan', name: 'Dan Brooks', blurb: 'Coach at School A · teacher at School B (two anchors)' },
  { id: 'u-leo', name: 'Leo Park', blurb: 'Guest player · roster only, no anchor anywhere' }
];

// UGP analogs. `info` = role-extension data on an anchor (student_info style).
// `data` = data generated inside an extending membership.
const INITIAL_MEMBERSHIPS = [
  { id: 'm-jordan-student', userId: 'u-jordan', groupId: 'g-students', window: 'Aug 2026 – Jun 2027',
    info: { 'Grade level': '11', 'Track': 'Track A', 'Status': 'Enrolled' } },
  { id: 'm-jordan-roster', userId: 'u-jordan', groupId: 'g-roster', window: 'Sep 2026 – Dec 2026',
    data: {
      position: 'Point Guard', number: 7, ppg: 14.2,
      games: [
        { opponent: 'Eastside High', points: 18, result: 'W 64–58' },
        { opponent: 'North Prep', points: 11, result: 'L 51–55' },
        { opponent: 'Lakeview', points: 21, result: 'W 70–49' }
      ]
    } },
  { id: 'm-jordan-chess', userId: 'u-jordan', groupId: 'g-chess', window: 'Aug 2026 – Jun 2027' },
  { id: 'm-maya-exchange', userId: 'u-maya', groupId: 'g-exchange', window: 'Aug 2026 – Jun 2027',
    info: { 'Grade level': '10', 'Home school': 'IES Madrid', 'Status': 'Enrolled' } },
  { id: 'm-maya-chess', userId: 'u-maya', groupId: 'g-chess', window: 'Sep 2026 – Jun 2027' },
  { id: 'm-dan-coach', userId: 'u-dan', groupId: 'g-coaches', window: 'Open-ended',
    info: { 'Title': 'Head Coach', 'Certification': 'State Level II' } },
  { id: 'm-dan-teacher', userId: 'u-dan', groupId: 'g-teachers-b', window: 'Open-ended',
    info: { 'Title': 'PE Teacher', 'Department': 'Athletics' } },
  { id: 'm-leo-roster', userId: 'u-leo', groupId: 'g-roster', window: 'Sep 2026 – Dec 2026',
    data: { position: 'Center', number: 15, ppg: 8.1, games: [] } }
];

// Designations = relationship badges on the anchor UGP. No group, no data.
const DESIGNATIONS = [
  { userId: 'u-jordan', orgId: 'schoolA', label: 'Dean\u2019s List', term: 'Fall 2026' }
];

/* ------------------------------------------------------------------ */

let nextId = 1;
const newId = (prefix) => `${prefix}-new-${nextId++}`;

function groupById(groups, id) {
  return groups.find((g) => g.id === id) || null;
}

function groupPath(groups, id) {
  const parts = [];
  let g = groupById(groups, id);
  while (g) {
    parts.unshift(g.name);
    g = g.parentId ? groupById(groups, g.parentId) : null;
  }
  return parts;
}

function buildTree(groups, orgId) {
  const byParent = {};
  groups.filter((g) => g.orgId === orgId).forEach((g) => {
    const key = g.parentId || '__root__';
    (byParent[key] = byParent[key] || []).push(g);
  });
  const attach = (parentKey) =>
    (byParent[parentKey] || []).map((g) => ({ ...g, children: attach(g.id) }));
  return attach('__root__');
}

function KindPill({ group }) {
  if (group.structural) return <span className="mmd-pill mmd-pill-structural">structural</span>;
  if (group.kind === 'anchor') return <span className="mmd-pill mmd-pill-anchor">anchor</span>;
  return <span className="mmd-pill mmd-pill-extending">extending</span>;
}

function TreeNode({ node, memberships }) {
  const memberCount = memberships.filter((m) => m.groupId === node.id).length;
  return (
    <li>
      <div className="mmd-tree-row">
        <span className="mmd-tree-name">{node.name}</span>
        <KindPill group={node} />
        {node.periodLabel && <span className="mmd-tree-period">{node.periodLabel}</span>}
        {memberCount > 0 && <span className="mmd-tree-count">{memberCount} member{memberCount === 1 ? '' : 's'}</span>}
        {GROUP_GRANTS[node.id] && <span className="mmd-tree-grants" title={`Grants: ${GROUP_GRANTS[node.id].join(', ')}`}>grants</span>}
      </div>
      {node.children.length > 0 && (
        <ul>{node.children.map((c) => <TreeNode key={c.id} node={c} memberships={memberships} />)}</ul>
      )}
    </li>
  );
}

/* ------------------------------------------------------------------ */

export default function MembershipModelDemo() {
  const [groups, setGroups] = useState(INITIAL_GROUPS);
  const [memberships, setMemberships] = useState(INITIAL_MEMBERSHIPS);
  const [selectedUserId, setSelectedUserId] = useState('u-jordan');
  const [anchorChoice, setAnchorChoice] = useState({}); // userId -> membershipId
  const [view, setView] = useState({ type: 'profile' }); // or { type: 'membership', membershipId }

  // Create-group form
  const [cgName, setCgName] = useState('');
  const [cgOrg, setCgOrg] = useState('schoolA');
  const [cgParent, setCgParent] = useState('');
  const [cgKind, setCgKind] = useState('extending');
  const [cgMsg, setCgMsg] = useState(null);

  // Add-member form
  const [amUser, setAmUser] = useState('u-leo');
  const [amGroup, setAmGroup] = useState('g-chess');
  const [amMsg, setAmMsg] = useState(null);

  const user = INITIAL_USERS.find((u) => u.id === selectedUserId);
  const userMemberships = useMemo(
    () => memberships.filter((m) => m.userId === selectedUserId),
    [memberships, selectedUserId]
  );

  const anchors = userMemberships.filter((m) => groupById(groups, m.groupId)?.kind === 'anchor');
  const chosen = anchorChoice[selectedUserId];
  const activeAnchor = anchors.find((m) => m.id === chosen) || anchors[0] || null;
  const activeGroup = activeAnchor ? groupById(groups, activeAnchor.groupId) : null;
  const ctxOrgId = activeGroup ? activeGroup.orgId : null;

  const extendingInOrg = userMemberships.filter((m) => {
    const g = groupById(groups, m.groupId);
    return g && g.kind === 'extending' && (ctxOrgId ? g.orgId === ctxOrgId : true);
  });

  const designations = DESIGNATIONS.filter(
    (d) => d.userId === selectedUserId && (!ctxOrgId || d.orgId === ctxOrgId)
  );

  // Effective permissions = anchor grants ∪ same-org extending grants
  const effectivePerms = useMemo(() => {
    const out = [];
    if (activeAnchor) {
      (GROUP_GRANTS[activeAnchor.groupId] || []).forEach((p) =>
        out.push({ page: p, source: `${activeGroup.name} (anchor)` })
      );
      extendingInOrg.forEach((m) => {
        const g = groupById(groups, m.groupId);
        (GROUP_GRANTS[m.groupId] || []).forEach((p) =>
          out.push({ page: p, source: `${g.name} (extending)` })
        );
      });
    }
    return out;
  }, [activeAnchor, activeGroup, extendingInOrg, groups]);

  const selectUser = (id) => {
    setSelectedUserId(id);
    setView({ type: 'profile' });
  };

  const switchAnchor = (membershipId) => {
    setAnchorChoice((prev) => ({ ...prev, [selectedUserId]: membershipId }));
    setView({ type: 'profile' });
  };

  const handleCreateGroup = (e) => {
    e.preventDefault();
    const name = cgName.trim();
    if (!name) {
      setCgMsg({ tone: 'error', text: 'Group name is required.' });
      return;
    }
    const group = {
      id: newId('g'),
      orgId: cgOrg,
      parentId: cgParent || null,
      name,
      kind: cgKind,
      periodLabel: '2026–2027'
    };
    setGroups((prev) => [...prev, group]);
    setCgName('');
    setCgMsg({
      tone: 'ok',
      text:
        cgKind === 'anchor'
          ? `Created anchor group "${name}". In the real app this choice would be permission-gated (provisioning roles only).`
          : `Created extending group "${name}". Members will never see it in the switcher.`
    });
  };

  const handleAddMember = (e) => {
    e.preventDefault();
    const targetGroup = groupById(groups, amGroup);
    const targetUser = INITIAL_USERS.find((u) => u.id === amUser);
    if (!targetGroup || !targetUser) return;

    const theirMemberships = memberships.filter((m) => m.userId === amUser);
    const already = theirMemberships.some((m) => m.groupId === amGroup);
    if (already) {
      setAmMsg({ tone: 'error', text: `${targetUser.name} is already a member of ${targetGroup.name}.` });
      return;
    }

    const anchorInOrg = theirMemberships.find((m) => {
      const g = groupById(groups, m.groupId);
      return g && g.kind === 'anchor' && g.orgId === targetGroup.orgId;
    });

    if (targetGroup.kind === 'anchor' && anchorInOrg) {
      const existing = groupById(groups, anchorInOrg.groupId);
      setAmMsg({
        tone: 'error',
        text: `Blocked: ${targetUser.name} already has an anchor in ${ORGS[targetGroup.orgId].name} (${existing.name}). One anchor per user per org at a time — end that enrollment first, or model this as an extending group.`
      });
      return;
    }

    const membership = {
      id: newId('m'),
      userId: amUser,
      groupId: amGroup,
      window: 'Sep 2026 – Jun 2027'
    };
    setMemberships((prev) => [...prev, membership]);

    if (targetGroup.kind === 'extending' && !anchorInOrg) {
      setAmMsg({
        tone: 'warn',
        text: `Added, with a caveat: ${targetUser.name} has no anchor in ${ORGS[targetGroup.orgId].name}. They will appear on the ${targetGroup.name} roster but cannot sign in there, and any grants on ${targetGroup.name} give them nothing (grants attach on top of an anchor).`
      });
    } else {
      setAmMsg({ tone: 'ok', text: `Added ${targetUser.name} to ${targetGroup.name} (${targetGroup.kind}).` });
    }
  };

  /* ---------------- membership detail view ---------------- */

  const renderMembershipDetail = (membershipId) => {
    const m = memberships.find((x) => x.id === membershipId);
    if (!m) return null;
    const g = groupById(groups, m.groupId);
    const path = groupPath(groups, g.id);
    const apps = GROUP_APPS[g.id] || [];
    const grants = GROUP_GRANTS[g.id] || [];
    const roster = memberships
      .filter((x) => x.groupId === g.id)
      .map((x) => {
        const ru = INITIAL_USERS.find((u) => u.id === x.userId);
        const theirAnchor = memberships.find((a) => {
          const ag = groupById(groups, a.groupId);
          return a.userId === x.userId && ag && ag.kind === 'anchor' && ag.orgId === g.orgId;
        });
        return { user: ru, anchorGroup: theirAnchor ? groupById(groups, theirAnchor.groupId) : null };
      });

    const rich = Boolean(m.data && apps.length);

    return (
      <div className="mmd-card mmd-detail">
        <button className="mmd-back" onClick={() => setView({ type: 'profile' })}>
          ← Back to {user.name.split(' ')[0]}&rsquo;s profile
        </button>
        <div className="mmd-detail-breadcrumb">{ORGS[g.orgId].name} › {path.join(' › ')}</div>
        <h2>{g.name} {g.periodLabel && <span className="mmd-detail-season">· {g.periodLabel}</span>}</h2>
        <p className="mmd-muted">
          Membership detail view — opened by clicking the section on the anchor profile,
          <strong> not</strong> via Switch Context. This membership belongs to {user.name}; window: {m.window}.
        </p>

        {rich && (
          <>
            <div className="mmd-statgrid">
              <div className="mmd-stat"><span className="mmd-stat-label">Position</span><span className="mmd-stat-value">{m.data.position}</span></div>
              <div className="mmd-stat"><span className="mmd-stat-label">Number</span><span className="mmd-stat-value">#{m.data.number}</span></div>
              <div className="mmd-stat"><span className="mmd-stat-label">PPG</span><span className="mmd-stat-value">{m.data.ppg}</span></div>
            </div>
            {m.data.games.length > 0 && (
              <table className="mmd-table">
                <thead><tr><th>Opponent</th><th>Points</th><th>Result</th></tr></thead>
                <tbody>
                  {m.data.games.map((game, i) => (
                    <tr key={i}><td>{game.opponent}</td><td>{game.points}</td><td>{game.result}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {!rich && (
          <p className="mmd-note">
            Thin membership — no apps assigned to this group, so there is nothing beyond the
            roster and window. If this club starts collecting data, assign apps to its
            group-period; nothing else changes.
          </p>
        )}

        <h3>Roster</h3>
        <ul className="mmd-roster">
          {roster.map((r) => (
            <li key={r.user.id}>
              <span>{r.user.name}</span>
              {r.anchorGroup
                ? <span className="mmd-roster-anchor">anchor: {r.anchorGroup.name}</span>
                : <span className="mmd-roster-noanchor">no anchor — roster only</span>}
            </li>
          ))}
        </ul>
        {g.id === 'g-chess' && (
          <p className="mmd-note">
            Note the roster mixes members from different anchors (Students <em>and</em> Exchange
            Students). Nesting under Students never restricted who can join — placement is navigation only.
          </p>
        )}

        {apps.length > 0 && (
          <p className="mmd-muted">Apps on this group-period: {apps.join(' · ')}</p>
        )}
        {grants.length > 0 && (
          <p className="mmd-muted">
            Grants contributed to members (additively, same org, requires an anchor): {grants.join(' · ')}
          </p>
        )}
      </div>
    );
  };

  /* ---------------- profile view ---------------- */

  const renderProfile = () => (
    <div className="mmd-card">
      <div className="mmd-profile-head">
        <div className="mmd-avatar">{user.name.split(' ').map((p) => p[0]).join('')}</div>
        <div>
          <h2>{user.name}</h2>
          {activeGroup ? (
            <p className="mmd-muted">
              Anchor: <strong>{activeGroup.name}</strong> · {ORGS[activeGroup.orgId].name}
              {activeGroup.periodLabel ? ` · ${activeGroup.periodLabel}` : ''} · {activeAnchor.window}
            </p>
          ) : (
            <p className="mmd-muted">No anchor membership — no sign-in identity.</p>
          )}
          {designations.map((d) => (
            <span key={d.label + d.term} className="mmd-badge" title="Designation — a relationship row on the anchor UGP. No group, no data of its own; the evidence stays on the anchor.">
              ★ {d.label} · {d.term}
            </span>
          ))}
        </div>
      </div>

      {activeAnchor && activeAnchor.info && (
        <div className="mmd-section">
          <h3>{activeGroup.name === 'Coaches' || activeGroup.name === 'Teachers' ? 'Staff Information' : 'Student Information'} <span className="mmd-section-src">from anchor UGP</span></h3>
          <div className="mmd-kv">
            {Object.entries(activeAnchor.info).map(([k, v]) => (
              <div key={k}><span>{k}</span><strong>{v}</strong></div>
            ))}
          </div>
        </div>
      )}

      {extendingInOrg.length > 0 && (
        <div className="mmd-section">
          <h3>Memberships <span className="mmd-section-src">extending UGPs in {ctxOrgId ? ORGS[ctxOrgId].name : 'any org'} — click to open</span></h3>
          {extendingInOrg.map((m) => {
            const g = groupById(groups, m.groupId);
            const apps = GROUP_APPS[g.id] || [];
            const rich = Boolean(m.data && apps.length);
            return (
              <button
                key={m.id}
                className={rich ? 'mmd-membership mmd-membership-rich' : 'mmd-membership'}
                onClick={() => setView({ type: 'membership', membershipId: m.id })}
              >
                <div className="mmd-membership-main">
                  <span className="mmd-membership-name">{groupPath(groups, g.id).slice(-2).join(' › ')}</span>
                  <span className="mmd-membership-meta">{g.periodLabel || ''} · {m.window}</span>
                </div>
                {rich ? (
                  <div className="mmd-membership-preview">
                    <span>{m.data.position} · #{m.data.number} · {m.data.ppg} PPG</span>
                    <span className="mmd-open">Open membership profile →</span>
                  </div>
                ) : (
                  <span className="mmd-open">View →</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {!activeAnchor && extendingInOrg.length > 0 && (
        <p className="mmd-note">
          {user.name} appears on rosters (staff can still open the membership views above),
          but with no anchor they cannot sign in and receive nothing from any grants on those groups.
        </p>
      )}
    </div>
  );

  /* ---------------- render ---------------- */

  const orgGroupsForForms = groups.filter((g) => g.orgId === cgOrg);

  return (
    <div className="mmd-page">
      <header className="mmd-header">
        <h1>Anchor &amp; Extending Memberships — demo</h1>
        <p>
          Interactive sandbox for <code>docs/anchor-and-extending-memberships.md</code>.
          All data is mock and in-memory; nothing touches the API or real groups.{' '}
          <Link to="/membership-model-guide">Read the full guide</Link>.
        </p>
      </header>

      <div className="mmd-layout">
        {/* ---------- left: structure ---------- */}
        <aside className="mmd-side">
          <div className="mmd-card">
            <h3>Organization trees</h3>
            {Object.values(ORGS).map((org) => (
              <div key={org.id} className="mmd-org">
                <div className="mmd-org-name">{org.name}</div>
                <ul className="mmd-tree">
                  {buildTree(groups, org.id).map((n) => (
                    <TreeNode key={n.id} node={n} memberships={memberships} />
                  ))}
                </ul>
              </div>
            ))}
            <p className="mmd-note">
              Kind and placement are independent: Coaches is a nested <em>anchor</em>
              (a coach signs in there); Robotics is a top-level <em>extending</em> group;
              Chess Club nested under Students accepts members from any anchor.
            </p>
          </div>

          <div className="mmd-card">
            <h3>Create a group</h3>
            <form onSubmit={handleCreateGroup} className="mmd-form">
              <label>Name
                <input value={cgName} onChange={(e) => setCgName(e.target.value)} placeholder="e.g. Debate Club" />
              </label>
              <label>Organization
                <select value={cgOrg} onChange={(e) => { setCgOrg(e.target.value); setCgParent(''); }}>
                  {Object.values(ORGS).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </label>
              <label>Parent group
                <select value={cgParent} onChange={(e) => setCgParent(e.target.value)}>
                  <option value="">— top level (under organization) —</option>
                  {orgGroupsForForms.map((g) => (
                    <option key={g.id} value={g.id}>{groupPath(groups, g.id).join(' › ')}</option>
                  ))}
                </select>
              </label>
              <div className="mmd-kind-toggle">
                <label className={cgKind === 'extending' ? 'mmd-kind mmd-kind-active' : 'mmd-kind'}>
                  <input type="radio" name="cg-kind" checked={cgKind === 'extending'} onChange={() => setCgKind('extending')} />
                  <strong>Extending</strong> — members just belong (clubs, teams, cohorts). Default.
                </label>
                <label className={cgKind === 'anchor' ? 'mmd-kind mmd-kind-active' : 'mmd-kind'}>
                  <input type="radio" name="cg-kind" checked={cgKind === 'anchor'} onChange={() => setCgKind('anchor')} />
                  <strong>Anchor</strong> — members act as this role (sign-in identity). Rare; provisioning-time.
                </label>
              </div>
              <button type="submit" className="mmd-btn">Create group</button>
              {cgMsg && <p className={`mmd-msg mmd-msg-${cgMsg.tone}`}>{cgMsg.text}</p>}
            </form>
          </div>

          <div className="mmd-card">
            <h3>Add a member</h3>
            <form onSubmit={handleAddMember} className="mmd-form">
              <label>User
                <select value={amUser} onChange={(e) => setAmUser(e.target.value)}>
                  {INITIAL_USERS.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </label>
              <label>Group
                <select value={amGroup} onChange={(e) => setAmGroup(e.target.value)}>
                  {groups.filter((g) => !g.structural).map((g) => (
                    <option key={g.id} value={g.id}>
                      {ORGS[g.orgId].name} › {groupPath(groups, g.id).join(' › ')} ({g.kind})
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" className="mmd-btn">Add member</button>
              {amMsg && <p className={`mmd-msg mmd-msg-${amMsg.tone}`}>{amMsg.text}</p>}
            </form>
            <p className="mmd-note">
              Try: add <strong>Leo Park</strong> (no anchor) to Chess Club → allowed with a warning.
              Add <strong>Jordan</strong> to Exchange Students → blocked (one anchor per org).
            </p>
          </div>
        </aside>

        {/* ---------- right: experience simulation ---------- */}
        <main className="mmd-main">
          <div className="mmd-card">
            <h3>Pick a user to simulate</h3>
            <div className="mmd-userchips">
              {INITIAL_USERS.map((u) => (
                <button
                  key={u.id}
                  className={u.id === selectedUserId ? 'mmd-chip mmd-chip-active' : 'mmd-chip'}
                  onClick={() => selectUser(u.id)}
                >
                  <strong>{u.name}</strong>
                  <span>{u.blurb}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mmd-card mmd-context">
            <div className="mmd-context-row">
              <h3>Switch Context</h3>
              <span className="mmd-section-src">anchors only — extending memberships never appear here</span>
            </div>
            {anchors.length === 0 ? (
              <p className="mmd-msg mmd-msg-warn">
                {user.name} has no anchor membership anywhere — they cannot sign in.
                Their extending membership keeps them on rosters only.
              </p>
            ) : (
              <div className="mmd-anchor-switch">
                {anchors.map((m) => {
                  const g = groupById(groups, m.groupId);
                  return (
                    <button
                      key={m.id}
                      className={activeAnchor && activeAnchor.id === m.id ? 'mmd-anchorbtn mmd-anchorbtn-active' : 'mmd-anchorbtn'}
                      onClick={() => switchAnchor(m.id)}
                    >
                      {g.name} · {ORGS[g.orgId].name}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mmd-perms">
              <h4>Effective permissions {ctxOrgId ? `in ${ORGS[ctxOrgId].name}` : ''}</h4>
              {effectivePerms.length === 0 ? (
                <p className="mmd-muted">None — no anchor, so extending grants have nothing to attach to.</p>
              ) : (
                <ul>
                  {effectivePerms.map((p, i) => (
                    <li key={i}>
                      <code>{p.page}</code> <span className="mmd-perm-src">← {p.source}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mmd-note">
                Union = anchor grants ∪ same-org extending grants. Anchors never union with each
                other — switch Dan between Coach (School A) and Teacher (School B) to see scopes stay separate.
              </p>
            </div>
          </div>

          {view.type === 'profile' ? renderProfile() : renderMembershipDetail(view.membershipId)}

          <div className="mmd-card">
            <h3>Future (not in this sandbox)</h3>
            <ul className="mmd-note" style={{ margin: 0, paddingLeft: '1.2rem' }}>
              <li>
                <strong>Ancestor inheritance (opt-in):</strong> relationships / grants hanging on
                Sports (or the org) apply to members enrolled under descendants — without a direct
                UGP on that parent. Today everything is direct-membership only.
              </li>
              <li>
                <strong>Org / empty containers:</strong> keep <code>membership_kind</code> as{' '}
                <code>anchor</code> | <code>extending</code> (no NULL). Mark organization + Sports /
                Basketball shells <code>extending</code> — not a sign-in identity. Full notes:{' '}
                <Link to="/membership-model-guide#future-tasks">guide · future tasks</Link>.
              </li>
            </ul>
          </div>
        </main>
      </div>
    </div>
  );
}
